import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import ImportExportBar from "../../shared/ImportExportBar";
import PendingReceiptsTable from "../../shared/PendingReceiptsTable";
import { TableToolbar, FilterField, FilterInput, FilterCheckGroup, SortButton } from "../../shared/TableToolbar";
import { usePendingReceiptsCxP } from "../hooks/useReceiptCxP";
import SaldoInicialCxPModal from "../components/SaldoInicialCxPModal";
import AccountingDetailModal from "../../shared/AccountingDetailModal";
import { overlayClose } from "../../../../shared/utils/overlayClose";

// ── Estilos del modal — theme-aware ──────────────────────────────────────────
const OVERLAY = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  backdropFilter: "blur(3px)", zIndex: 1000,
};
const MODAL = {
  position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
  background: "var(--bg-card)", border: "1px solid var(--border-card)",
  borderRadius: 12, padding: 24,
  width: "min(500px,94vw)", zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
  maxHeight: "90vh", overflowY: "auto",
};
const LABEL = {
  display: "block", fontSize: "0.78rem", fontWeight: 600,
  color: "var(--text-secondary)", marginBottom: 5,
};
const INP = {
  width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-input)",
  borderRadius: 8, padding: "9px 12px", color: "var(--text-primary)", fontSize: "0.9rem",
  outline: "none", boxSizing: "border-box", transition: "border-color 0.15s",
};

// Deriva proveedor y factura(s) de un recibo CxP, ya sea de pago directo a una
// factura (factura_cxp) o de un pago multi-factura aplicado por detalles. Cubre
// también los recibos importados sin aplicar que ya traen cuenta_proveedor.
function reciboProveedorInfo(r) {
  const fac          = r.factura_cxp;
  const facturasDet  = (r.detalles ?? []).map((d) => d.factura_cxp).filter(Boolean);
  const facPrincipal = fac ?? facturasDet[0] ?? null;
  const prov         = facPrincipal?.cuenta_proveedor?.nombre ?? r.cuenta_proveedor_nombre ?? "—";
  const multi        = !fac && facturasDet.length > 1;
  const rawDoc       = facPrincipal?.numero_doc_proveedor?.trim() || "";
  const docProv      = (rawDoc && rawDoc !== facPrincipal?.numero_factura) ? rawDoc : "—";
  const numFactura   = multi ? `${facturasDet.length} facturas` : (facPrincipal?.numero_factura ?? null);
  return { facPrincipal, prov, multi, docProv, numFactura, nFacturas: facturasDet.length };
}

/** "2026-04-15" → "15/04/2026" */
function fmtDateRecibo(iso) {
  if (!iso || iso.length < 10) return iso ?? "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const METODO_LABEL = {
  TRANSFERENCIA: "recibos.transferencia", EFECTIVO: "recibos.efectivo",
  CHEQUE: "recibos.cheque", TARJETA: "recibos.tarjeta",
};

// ── Modal Ver detalle — mismo diseño que el detalle de Factura CxP ────────────
function VerModal({ r, onClose, onEdit, t }) {
  const info   = reciboProveedorInfo(r);
  const banco  = r.cuenta_bancaria?.banco ?? "—";
  const moneda = (r.moneda_pago || "CRC").toUpperCase();
  const fmt = (n) => Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));
  const montoFmt = moneda === "USD" ? `$ ${fmt(r.monto)}` : `₡${fmt(r.monto)}`;

  return (
    <AccountingDetailModal
      onClose={onClose} onEdit={onEdit}
      icon="fa-money-bill-transfer" iconColor="#f97316" iconBg="rgba(249,115,22,0.15)"
      title={`${t("cxp.detalleRecibo")} ${r.numero_recibo}`}
      badge={r.metodo_pago ? t(METODO_LABEL[r.metodo_pago] ?? r.metodo_pago) : null}
      subtitle={`${t("recibos.fechaPago")}: ${fmtDateRecibo(r.fecha_pago)}`}
      infoTitle={t("cxp.informacionGeneral")} infoIcon="fa-circle-info"
      infoRows={[
        [t("cxp.proveedor"),          info.prov],
        [t("cxp.facturaProveedor"),   info.docProv],
        [t("cxp.numFacturaInt"),      info.numFactura ?? "—"],
        [t("recibos.metodoPago"),     r.metodo_pago ? t(METODO_LABEL[r.metodo_pago] ?? r.metodo_pago) : "—"],
        [t("recibos.cuentaBancaria"), `${banco} — ${r.cuenta_bancaria?.numero_cuenta ?? ""}`],
        [t("common.referencia"),      r.documento || "—"],
        [t("cxp.montoPagado"), montoFmt, true],
      ]}
      contableTitle={t("cxp.detalleContable")} contableIcon="fa-scale-balanced"
      debeLabel={t("cxp.proveedorDebeRecibo")}
      debeCuenta={info.prov !== "—" ? info.prov : t("cxp.proveedor")}
      debeColor="#f87171" debeBg="rgba(248,113,113,0.15)"
      haberLabel={t("cxp.bancoHaberRecibo")}
      haberCuenta={banco}
      haberColor="#60a5fa" haberBg="rgba(96,165,250,0.15)"
      montoFmt={montoFmt}
    />
  );
}

// ── Modal Editar — theme-aware ────────────────────────────────────────────────
function EditModal({ r, onClose, onSaved, t }) {
  const [cuentas, setCuentas] = useState([]);
  const [form, setForm] = useState({
    fecha_pago:      r.fecha_pago,
    metodo_pago:     r.metodo_pago,
    documento:       r.documento ?? "",
    descripcion:     r.descripcion ?? "",
    cuenta_bancaria: r.cuenta_bancaria?.id ?? r.cuenta_bancaria ?? "",
  });
  const cuentaOriginalId = r.cuenta_bancaria?.id ?? r.cuenta_bancaria;
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => {
    apiFetch("/api/finance/cuentaBancaria/")
      .then(r => r.json())
      .then(d => setCuentas(d.results ?? d))
      .catch(() => {});
  }, []);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.fecha_pago)        { setError("La fecha es obligatoria."); return; }
    if (!form.documento.trim())  { setError("La referencia es obligatoria."); return; }
    setSaving(true); setError("");
    try {
      const res = await apiFetch(`/api/finance/reciboCxP/${r.id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          fecha_pago:      form.fecha_pago,
          metodo_pago:     form.metodo_pago,
          documento:       form.documento,
          descripcion:     form.descripcion,
          cuenta_bancaria: Number(form.cuenta_bancaria),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? "Error al actualizar el recibo.");
      }
      onSaved("Recibo actualizado correctamente.");
      onClose();
    } catch (ex) { setError(ex.message); }
    finally { setSaving(false); }
  }

  const field = (label, children) => (
    <div>
      <label style={LABEL}>{label}</label>
      {children}
    </div>
  );

  return (
    <>
      <div {...overlayClose(onClose)} style={OVERLAY}/>
      <div style={MODAL}>
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 16, paddingBottom: 12,
          borderBottom: "1px solid var(--border-card)",
        }}>
          <div>
            <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
              <i className="fa-solid fa-square-pen" style={{ color: "#f97316", marginRight: 8 }}/>
              {t("cxp.editarRecibo")}
            </h3>
            <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: "0.8rem" }}>
              {r.numero_recibo} · Original ₡{Number(r.monto).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none",
            color: "var(--text-muted)", cursor: "pointer", fontSize: "1.2rem" }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>

        {/* Monto fijo — no editable */}
        <div style={{
          padding: "8px 12px", background: "var(--hover-bg)",
          border: "1px solid var(--border-card)", borderRadius: 8, marginBottom: 4,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ color: "var(--text-secondary)", fontSize: "0.8rem" }}>
            {t("cxp.montoNoModificable")}
          </span>
          <span style={{ color: "var(--text-primary)", fontWeight: 700, fontFamily: "monospace" }}>
            ₡{Number(r.monto).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
          {field(`${t("recibos.fechaPago")} *`,
            <input type="date" style={INP} value={form.fecha_pago}
              onChange={e => set("fecha_pago", e.target.value)}
              onFocus={e => e.target.style.borderColor="#f97316"}
              onBlur={e  => e.target.style.borderColor="var(--border-input)"}/>
          )}
          {field(t("recibos.metodoPago"),
            <select style={{ ...INP, cursor: "pointer" }} value={form.metodo_pago}
              onChange={e => set("metodo_pago", e.target.value)}>
              <option value="TRANSFERENCIA">{t("recibos.transferencia")}</option>
              <option value="EFECTIVO">{t("recibos.efectivo")}</option>
              <option value="CHEQUE">{t("recibos.cheque")}</option>
              <option value="TARJETA">{t("recibos.tarjeta")}</option>
            </select>
          )}
          <div style={{ gridColumn: "span 2" }}>
            {field(t("recibos.cuentaBancaria"),
              <>
                <select style={{ ...INP, cursor: "pointer" }} value={form.cuenta_bancaria}
                  onChange={e => set("cuenta_bancaria", e.target.value)}>
                  {cuentas.map(c => (
                    <option key={c.id} value={c.id}>{c.banco} — {c.numero_cuenta}</option>
                  ))}
                </select>
                {String(form.cuenta_bancaria) !== String(cuentaOriginalId) && (
                  <div style={{
                    marginTop: 5, padding: "6px 10px",
                    background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)",
                    borderRadius: 6, color: "#fbbf24", fontSize: "0.78rem",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <i className="fa-solid fa-triangle-exclamation"/>
                    {t("cxp.asientoRevertido")}
                  </div>
                )}
              </>
            )}
          </div>
          <div style={{ gridColumn: "span 2" }}>
            {field(`${t("cxp.referenciaDoc")} *`,
              <input type="text" style={INP} value={form.documento}
                placeholder="N° transferencia, cheque, etc."
                onChange={e => set("documento", e.target.value)}
                onFocus={e => e.target.style.borderColor="#f97316"}
                onBlur={e  => e.target.style.borderColor="var(--border-input)"}/>
            )}
          </div>
          <div style={{ gridColumn: "span 2" }}>
            {field(
              <span>
                {t("common.descripcion")}{" "}
                <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: "0.75rem" }}>
                  ({t("common.opcional")} — anexo bancario)
                </span>
              </span>,
              <input type="text" style={INP} value={form.descripcion}
                placeholder="Texto para el anexo bancario"
                onChange={e => set("descripcion", e.target.value)}
                onFocus={e => e.target.style.borderColor="#f97316"}
                onBlur={e  => e.target.style.borderColor="var(--border-input)"}/>
            )}
          </div>
        </div>

        {error && (
          <div style={{
            marginTop: 12, padding: "10px 14px",
            background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 8, color: "#f87171", fontSize: "0.84rem",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <i className="fa-solid fa-circle-exclamation"/>{error}
          </div>
        )}

        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16,
          paddingTop: 12, borderTop: "1px solid var(--border-card)",
        }}>
          <button onClick={onClose} disabled={saving} style={{
            padding: "8px 16px",
            background: "transparent", border: "1px solid var(--border-card)",
            borderRadius: 7, color: "var(--text-secondary)", fontSize: "0.84rem", cursor: "pointer",
          }}>
            {t("common.cancelar")}
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "8px 20px",
            background: saving ? "rgba(249,115,22,0.5)" : "#f97316",
            color: "#fff", border: "none", borderRadius: 7, fontSize: "0.84rem",
            fontWeight: 600, cursor: saving ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 7,
          }}>
            {saving
              ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("cxp.guardando")}</>
              : <><i className="fa-solid fa-floppy-disk"/> {t("cxp.guardarCambios")}</>}
          </button>
        </div>
      </div>
    </>
  );
}

/** "2026-04-15" → "15/04/2026" */
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// ── PillBtn — mismo estilo de ícono circular que ReceiptRow.js (CxC) ────────
function PillBtn({ icon, label, color, hoverBg, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 30, height: 30, padding: 0,
        background: `${color}1a`, border: `1px solid ${color}4d`,
        borderRadius: 6, color, fontSize: "0.85rem", cursor: "pointer",
        transition: "background 0.15s, border-color 0.15s",
      }}
      onMouseOver={(e) => { e.currentTarget.style.background = hoverBg; e.currentTarget.style.borderColor = color; }}
      onMouseOut={(e) => { e.currentTarget.style.background = `${color}1a`; e.currentTarget.style.borderColor = `${color}4d`; }}
    >
      <i className={`fa-solid ${icon}`} />
    </button>
  );
}

// ── ActionButtons ─────────────────────────────────────────────────────────────
function ActionButtons({ onVer, onEdit, onDelete, t }) {
  return (
    <div style={{ display: "flex", gap: 5, justifyContent: "center" }}>
      {onVer && (
        <PillBtn icon="fa-eye" label={t("cxp.verDetalle")} color="#60a5fa"
          hoverBg="rgba(96,165,250,0.15)" onClick={onVer} />
      )}
      {onEdit && (
        <PillBtn icon="fa-pencil" label={t("common.editar")} color="#f97316"
          hoverBg="rgba(249,115,22,0.15)" onClick={onEdit} />
      )}
      {onDelete && (
        <PillBtn icon="fa-trash" label={t("common.eliminar")} color="#f87171"
          hoverBg="rgba(248,113,113,0.15)" onClick={onDelete} />
      )}
    </div>
  );
}

export default function ListReciboCxP() {
  const { t } = useTranslation();
  const [items,        setItems]        = useState([]);
  const [count,        setCount]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [search,       setSearch]       = useState("");
  const [fechaDesde,   setFechaDesde]   = useState("");
  const [fechaHasta,   setFechaHasta]   = useState("");
  const [montoMin,     setMontoMin]     = useState("");
  const [montoMax,     setMontoMax]     = useState("");
  const [metodoPago,   setMetodoPago]   = useState([]);
  const [cuentaBanco,  setCuentaBanco]  = useState([]);
  const [ordering,     setOrdering]     = useState("");
  const [cuentasFiltro, setCuentasFiltro] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [deleting,     setDeleting]     = useState(null);
  const [delLoad,      setDelLoad]      = useState(false);
  const [actionMsg,    setActionMsg]    = useState(null);
  const [viendo,       setViendo]       = useState(null);
  const [editando,     setEditando]     = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [pendingKey,   setPendingKey]   = useState(0);
  const [showSaldoModal, setShowSaldoModal] = useState(false);
  const { pending } = usePendingReceiptsCxP(pendingKey);

  const PAGE_SIZE  = 20;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const fetchData = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ page, page_size: PAGE_SIZE });
    if (search.trim())     qs.set("search", search.trim());
    if (fechaDesde.trim()) qs.set("fecha_desde", fechaDesde.trim());
    if (fechaHasta.trim()) qs.set("fecha_hasta", fechaHasta.trim());
    if (montoMin.trim())   qs.set("monto_min", montoMin.trim());
    if (montoMax.trim())   qs.set("monto_max", montoMax.trim());
    if (ordering.trim())   qs.set("ordering", ordering.trim());
    metodoPago.forEach((m) => qs.append("metodo_pago", m));
    cuentaBanco.forEach((c) => qs.append("cuenta_bancaria", String(c)));
    apiFetch(`/api/finance/reciboCxP/?${qs}`)
      .then(r => r.json())
      .then(d => { setItems(d.results ?? d ?? []); setCount(d.count ?? (d.results ?? d ?? []).length); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, search, fechaDesde, fechaHasta, montoMin, montoMax, ordering, metodoPago, cuentaBanco]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Cuentas bancarias del condominio para el panel de filtros (mismo endpoint que CxC)
  useEffect(() => {
    apiFetch("/api/finance/recibo/cuentas-filtro/")
      .then((r) => r.json())
      .then((d) => setCuentasFiltro(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  function showMsg(ok, text) {
    setActionMsg({ ok, text });
    setTimeout(() => setActionMsg(null), 4000);
  }

  const handleImportSuccess = useCallback(() => {
    setPage(1);
    fetchData();
    setPendingKey((k) => k + 1);
  }, [fetchData]);

  function handleImportResult(resultado) {
    setImportResult(resultado);
    setPendingKey((k) => k + 1);
  }

  async function handleDelete() {
    if (!deleting) return;
    setDelLoad(true);
    try {
      const res = await apiFetch(`/api/finance/reciboCxP/${deleting.id}/`, { method: "DELETE" });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Error al eliminar");
      }
      setDeleting(null);
      showMsg(true, `Recibo ${deleting.numero_recibo} eliminado correctamente.`);
      setItems(prev => prev.filter(i => i.id !== deleting.id));
      setCount(c => c - 1);
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setDelLoad(false);
    }
  }

  return (
    <>
      {/* Breadcrumb */}
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/cxp">
          <span className={invoiceStyles.inactive}> {t("cxp.titulo")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("sidebar.recibos")}</span>
      </span>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ marginBottom: 6 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("cxp.gestionRecibos")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("cxp.administraRecibos")}</p>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <ImportExportBar
            plantillaUrl="/api/finance/reciboCxP/plantilla-excel/"
            importarUrl="/api/finance/reciboCxP/importar-excel/"
            plantillaFilename="plantilla_recibos_cxp.xlsx"
            isReceipts
            onImportSuccess={handleImportSuccess}
            onResult={handleImportResult}
          />
          <button onClick={() => setShowSaldoModal(true)} className="btn"
            style={{
              padding: "7px 14px", background: "rgba(139,92,246,0.15)",
              border: "1px solid rgba(139,92,246,0.4)", borderRadius: 6,
              color: "#a78bfa", fontWeight: 400, fontSize: 14,
              display: "flex", alignItems: "center", gap: 7, cursor: "pointer",
            }}>
            <i className="fa-solid fa-scale-balanced" /> {t("saldoInicialCxp.titulo")}
          </button>
          <Link to="/finanzas/cxp/recibos/agregar" className="btn"
            style={{
              padding: "7px 14px", background: "#f97316", borderRadius: 6,
              color: "#fff", textDecoration: "none", fontWeight: 400,
              fontSize: 14, display: "flex", alignItems: "center", gap: 7, border: "none",
            }}>
            <i className="fa-solid fa-plus" /> {t("cxp.nuevoPago")}
          </Link>
        </div>
      </div>

      {/* Resultado de importación */}
      {importResult && (
        <div style={{
          width: "100%", borderRadius: 8, marginBottom: 14,
          background: importResult.ok ? "rgba(74,222,128,0.07)" : "rgba(248,113,113,0.07)",
          border: `1px solid ${importResult.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
          borderLeft: `4px solid ${importResult.ok ? "#4ade80" : "#f87171"}`,
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "10px 16px",
            borderBottom: importResult.errores?.length > 0 ? "1px solid var(--border-card)" : "none",
          }}>
            <span style={{ fontWeight: 600, fontSize: "0.88rem",
              color: importResult.ok ? "#bbf7d0" : "#fecaca" }}>
              {importResult.msg}
            </span>
            <button onClick={() => setImportResult(null)}
              style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "1rem" }}>✕</button>
          </div>
          {importResult.errores?.length > 0 && (
            <div style={{ padding: "10px 16px" }}>
              <details>
                <summary style={{ cursor: "pointer", color: "#fb923c", fontWeight: 600, fontSize: "0.82rem" }}>
                  {importResult.errores.length} {t("recibos.filasConErrores")} — clic para ver
                </summary>
                <ul style={{ marginTop: 8, paddingLeft: 18, color: "#fca5a5", fontSize: "0.8rem" }}>
                  {importResult.errores.map((e, i) => (
                    <li key={i}>
                      <strong>Fila {e.fila}:</strong>{" "}
                      {e.errores?.join(" · ") ?? e}
                      {e.advertencia && <span style={{ color: "#fbbf24", marginLeft: 6 }}>(advertencia)</span>}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      )}

      {/* Toast */}
      {actionMsg && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
          border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
          borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#f87171"}`,
          borderRadius: 8, padding: "12px 18px",
          display: "flex", alignItems: "center", gap: 10,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)", maxWidth: 380,
        }}>
          <i className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}
             style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontSize: "1.1rem" }} />
          <span style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontWeight: 600 }}>
            {actionMsg.text}
          </span>
        </div>
      )}

      {/* Recibos sin aplicar (pendientes) */}
      <PendingReceiptsTable
        pendientes={pending}
        pageSize={5}
        alwaysVisible
        applyBasePath="/finanzas/cxp/recibos"
        entityLabel={t("cxp.proveedor")}
      />

      {/* Modal Saldos Iniciales CxP */}
      {showSaldoModal && (
        <SaldoInicialCxPModal
          onClose={() => setShowSaldoModal(false)}
          onSuccess={() => { fetchData(); setPendingKey((k) => k + 1); }}
        />
      )}

      {/* Tabla */}
      <div className={invoiceStyles["table-container"]}>

        {/* Barra búsqueda + orden + filtros (mismo set que CxC Recibos) */}
        <TableToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder={t("cxp.buscarReciboPlaceholder")}
          loading={loading}
          sort={
            <SortButton
              state={ordering === "-numero_recibo" ? "desc" : ordering === "numero_recibo" ? "asc" : "none"}
              label={ordering === "-numero_recibo" ? t("recibos.nReciboDesc") : ordering === "numero_recibo" ? t("recibos.nReciboAsc") : t("common.ordenar")}
              onToggle={() => {
                setOrdering((c) => c === "" ? "-numero_recibo" : c === "-numero_recibo" ? "numero_recibo" : "");
                setPage(1);
              }}
            />
          }
          activeFilterCount={
            [fechaDesde, fechaHasta, montoMin, montoMax].filter(Boolean).length
            + metodoPago.length + cuentaBanco.length
          }
          onClearFilters={() => {
            setFechaDesde(""); setFechaHasta(""); setMontoMin(""); setMontoMax("");
            setMetodoPago([]); setCuentaBanco([]); setPage(1);
          }}
          filters={
            <>
              <FilterField label={t("common.fechaDesde")}>
                <FilterInput type="date" value={fechaDesde}
                  onChange={(e) => { setFechaDesde(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.fechaHasta")}>
                <FilterInput type="date" value={fechaHasta}
                  onChange={(e) => { setFechaHasta(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.montoMinimo")}>
                <FilterInput type="number" min="0" step="1000" placeholder="0" value={montoMin}
                  onChange={(e) => { setMontoMin(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.montoMaximo")}>
                <FilterInput type="number" min="0" step="1000" placeholder={t("common.sinLimite")} value={montoMax}
                  onChange={(e) => { setMontoMax(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.metodoPago")}>
                <FilterCheckGroup
                  options={[
                    { value: "TRANSFERENCIA", label: t("recibos.transferencia") },
                    { value: "EFECTIVO",      label: t("recibos.efectivo") },
                    { value: "CHEQUE",        label: t("recibos.cheque") },
                    { value: "TARJETA",       label: t("recibos.tarjeta") },
                  ]}
                  selected={metodoPago}
                  onChange={(next) => { setMetodoPago(next); setPage(1); }}
                />
              </FilterField>
              {cuentasFiltro.length > 0 && (
                <FilterField label={t("common.cuentaBancaria")}>
                  <FilterCheckGroup
                    options={cuentasFiltro.map((c) => ({ value: c.id, label: c.label }))}
                    selected={cuentaBanco}
                    onChange={(next) => { setCuentaBanco(next); setPage(1); }}
                  />
                </FilterField>
              )}
            </>
          }
        />

        {/* Tabla */}
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>{t("recibos.colRecibo")}</th>
                <th>{t("cxp.proveedor")}</th>
                <th>{t("cxp.facturaProveedor")}</th>
                <th className="text-end">{t("common.monto")}</th>
                <th>{t("common.fecha")}</th>
                <th>{t("recibos.colMetodo")}</th>
                <th>{t("recibos.colCuenta")}</th>
                <th className="text-center">{t("common.acciones")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 32, color: "var(--text-secondary)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin"
                     style={{ marginRight: 8, color: "#f97316" }} />
                  {t("cxp.cargandoRecibos")}
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-inbox" style={{ marginRight: 8, fontSize: "1.2rem" }} />
                  {t("cxp.sinRecibos")}.{" "}
                  <Link to="/finanzas/cxp/recibos/agregar" style={{ color: "#f97316" }}>
                    {t("cxp.registrarPrimero")}
                  </Link>
                </td></tr>
              ) : items.map(r => {
                const info  = reciboProveedorInfo(r);
                const prov  = info.prov;
                const banco = r.cuenta_bancaria?.banco ?? "—";
                const docProv = info.docProv;
                const METODO_LABEL = {
                  TRANSFERENCIA: t("recibos.transferencia"),
                  EFECTIVO:      t("recibos.efectivo"),
                  CHEQUE:        t("recibos.cheque"),
                  TARJETA:       t("cxp.tarjeta"),
                };

                return (
                  <tr key={r.id}>
                    {/* N° Recibo */}
                    <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                      {r.numero_recibo}
                    </td>

                    {/* Proveedor */}
                    <td style={{ color: "var(--text-primary)" }}>{prov}</td>

                    {/* Factura: NDocProveedor arriba, CP-XXXXX (o "N facturas") abajo */}
                    <td>
                      {info.multi ? (
                        <span style={{ fontFamily: "monospace", fontWeight: 700,
                          color: "var(--text-primary)", fontSize: "0.9rem" }}>
                          {info.numFactura}
                        </span>
                      ) : (
                        <>
                          <span style={{ fontFamily: "monospace", fontWeight: 700,
                            color: "var(--text-primary)", fontSize: "0.9rem" }}>
                            {docProv}
                          </span>
                          {info.facPrincipal?.numero_factura && (
                            <span style={{ display: "block", fontSize: "0.73rem",
                              color: "var(--text-muted)", fontFamily: "monospace",
                              fontWeight: 400, marginTop: 2 }}>
                              {info.facPrincipal.numero_factura}
                            </span>
                          )}
                        </>
                      )}
                    </td>

                    {/* Monto — mismo render que CxC (USD: $ + ≈ ₡; CRC: ₡) */}
                    <td className="text-end">
                      {(r.moneda_pago || 'CRC').toUpperCase() === 'USD' ? (
                        <span>
                          <span style={{ color: "#60a5fa", fontWeight: 600 }}>
                            ${Number(r.monto).toFixed(2)}
                          </span>
                          {Number(r.monto_colones) > 0 && (
                            <span style={{ display: "block", fontSize: "0.7rem", color: "var(--text-muted)" }}>
                              ≈ ₡{formatCurrency(r.monto_colones)}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span>₡{formatCurrency(r.monto)}</span>
                      )}
                    </td>

                    {/* Fecha DD/MM/YYYY */}
                    <td>{fmtDate(r.fecha_pago)}</td>

                    {/* Método de pago */}
                    <td>{METODO_LABEL[r.metodo_pago] ?? r.metodo_pago}</td>

                    {/* Cuenta bancaria */}
                    <td>{banco}</td>

                    {/* Acciones */}
                    <td className="text-center">
                      <ActionButtons t={t}
                        onVer={() => setViendo(r)}
                        onEdit={() => setEditando(r)}
                        onDelete={() => setDeleting(r)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer paginación */}
        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }} />
                {t("common.cargando")}
              </span>
            ) : (
              <>
                <span>{count}</span> {t("cxp.recibos")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{Math.max(1, totalPages)}</span>
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>
              {t("common.anterior")}
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>

      {viendo   && <VerModal  r={viendo}  t={t} onClose={() => setViendo(null)}
        onEdit={() => { setEditando(viendo); setViendo(null); }} />}
      {editando && <EditModal r={editando} t={t} onClose={() => setEditando(null)}
        onSaved={(msg) => {
          setActionMsg({ ok: true, text: msg });
          setTimeout(() => setActionMsg(null), 4000);
          setItems(prev => prev.map(i =>
            i.id === editando.id ? { ...i, ...editando } : i
          ));
          fetchData();
        }} />}

      {deleting && (
        <DeleteReciboCxPModal
          recibo={deleting}
          loading={delLoad}
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  );
}

// ── Modal eliminar recibo CxP ─────────────────────────────────────────────────
function DeleteReciboCxPModal({ recibo, loading, onConfirm, onCancel }) {
  const moneda = (recibo.moneda_pago || "CRC").toUpperCase();
  const sim    = moneda === "USD" ? "$ " : "₡ ";
  const monto  = Number(recibo.monto || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  const factura = recibo.factura_cxp;
  const info = reciboProveedorInfo(recibo);
  const proveedor = info.prov !== "—" ? info.prov : (factura?.concepto ?? null);

  return (
    <>
      <div {...overlayClose(onCancel)} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
        zIndex: 1000, backdropFilter: "blur(3px)",
      }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 12, padding: "24px 24px 20px",
        width: "min(520px,94vw)", zIndex: 1001,
        boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ background: "rgba(248,113,113,0.15)", borderRadius: 8, padding: "8px 10px", color: "#f87171" }}>
              <i className="fa-solid fa-trash" style={{ fontSize: "1.1rem" }} />
            </div>
            <div>
              <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
                Eliminar Recibo {recibo.numero_recibo}
              </h3>
              <p style={{ margin: "2px 0 0", color: "var(--text-muted)", fontSize: "0.78rem" }}>
                {sim}{monto}{proveedor ? ` · ${proveedor}` : ""}
              </p>
            </div>
          </div>
          <button onClick={onCancel} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "1.15rem", lineHeight: 1, padding: "2px 4px" }}>✕</button>
        </div>

        <div style={{ borderTop: "1px solid var(--border-card)", margin: "0 0 16px" }} />

        {/* Warning — tiene factura asociada */}
        {factura ? (
          <>
            <div style={{
              background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
              borderLeft: "3px solid #f87171", borderRadius: 6,
              padding: "9px 12px", color: "#fca5a5", fontSize: "0.83rem",
              marginBottom: 10, display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 700, marginBottom: 5 }}>Este pago tiene una factura asociada</div>
                Al eliminarlo, el saldo de la siguiente CxP volverá a quedar pendiente:
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  <li>
                    <strong>{factura.numero_factura ?? factura.id}</strong>
                    {" "}— abono de {sim}{monto}
                  </li>
                </ul>
              </div>
            </div>
            <div style={{
              background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)",
              borderLeft: "3px solid #fbbf24", borderRadius: 6,
              padding: "9px 12px", color: "#fde68a", fontSize: "0.83rem",
              marginBottom: 16,
            }}>
              <i className="fa-solid fa-circle-info" style={{ marginRight: 6 }} />
              El sistema revertirá automáticamente el abono en la CxP y el saldo bancario.
            </div>
          </>
        ) : (
          <div style={{
            background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
            borderLeft: "3px solid #f87171", borderRadius: 6,
            padding: "9px 12px", color: "#fca5a5", fontSize: "0.83rem",
            marginBottom: 16, display: "flex", gap: 8, alignItems: "flex-start",
          }}>
            <i className="fa-solid fa-circle-exclamation" style={{ marginTop: 2, flexShrink: 0 }} />
            <span>
              Se eliminará el pago de <strong>{sim}{monto}</strong> y se revertirá el saldo de la CxP y la cuenta bancaria.
              {" "}<strong>Esta acción no se puede deshacer.</strong>
            </span>
          </div>
        )}

        <div style={{ borderTop: "1px solid var(--border-card)", margin: "0 0 16px" }} />

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onCancel} disabled={loading} style={{
            padding: "8px 16px", background: "transparent",
            border: "1px solid var(--border-card)", borderRadius: 7,
            color: "var(--text-muted)", fontSize: "0.84rem", cursor: "pointer",
          }}>
            Cancelar
          </button>
          <button onClick={onConfirm} disabled={loading} style={{
            padding: "8px 18px",
            background: loading ? "rgba(248,113,113,0.4)" : "#ef4444",
            color: "#fff", border: "none", borderRadius: 7,
            fontSize: "0.84rem", fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 7,
          }}>
            {loading
              ? <><i className="fa-solid fa-circle-notch fa-spin" /> Eliminando…</>
              : <><i className="fa-solid fa-trash" /> {factura ? "Eliminar y revertir abono" : "Eliminar Recibo"}</>
            }
          </button>
        </div>
      </div>
    </>
  );
}
